import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LocalShipping as TruckIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  canMoveTruckToRegion,
  formatFboDate,
  formatFboFloat,
  formatFboNumber,
  getTruckCapacity,
  getTruckDate,
  getTruckId,
  getTruckPallets,
  getTruckRegion,
  getTruckStatusMeta,
  getTruckTypeLabel,
  isTruckUnderloaded,
  truckCellKey,
} from './fboSupplyPlanTruckRows.mjs';
import {
  getFboTruckItemName,
  prepareFboTruckItems,
} from './fboTruckItemList.mjs';

const TRUCK_DRAG_MIME = 'application/x-fbo-truck';

export function FboTruckCard({
  truck,
  readOnly = true,
  editable = false,
  dndEnabled = false,
  dragging = false,
  onOpen,
  onDragStart,
  onDragEnd,
}) {
  const capacity = getTruckCapacity(truck);
  const pallets = getTruckPallets(truck);
  const fillPercent = truck?.fill_percent
    ?? (capacity && pallets != null ? (Number(pallets) / capacity) * 100 : null);
  const status = getTruckStatusMeta(truck);
  const canEdit = editable && !readOnly;
  const canDrag = canEdit && dndEnabled && Boolean(getTruckId(truck));

  return (
    <Paper
      elevation={0}
      data-truck-id={getTruckId(truck)}
      sx={(theme) => ({
        width: '100%',
        minWidth: 170,
        p: 1,
        textAlign: 'left',
        userSelect: 'none',
        opacity: dragging ? 0.55 : 1,
        border: `1px solid ${theme.palette.outlineVariant || theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
      })}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TruckIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={700}>{getTruckTypeLabel(truck)} пал.</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {canDrag && (
              <Tooltip title="Перетащить фуру на другую дату" arrow>
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label={`Перетащить фуру ${getTruckId(truck) || ''}`}
                  draggable
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  sx={{ display: 'inline-flex', alignItems: 'center', cursor: dragging ? 'grabbing' : 'grab', color: 'text.secondary' }}
                >
                  <DragIcon fontSize="small" />
                </Box>
              </Tooltip>
            )}
            {onOpen && (
              <Tooltip title={canEdit ? 'Редактировать фуру' : 'Открыть состав'} arrow>
                <IconButton
                  size="small"
                  aria-label={`${canEdit ? 'Редактировать' : 'Открыть'} фуру ${getTruckId(truck) || ''}`}
                  onClick={onOpen}
                  sx={{ p: 0.25 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Chip size="small" color={status.color} variant="outlined" label={status.label} sx={{ height: 20 }} />
          </Stack>
        </Stack>
        <Box>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="caption" color="text.secondary">Загрузка</Typography>
            <Typography variant="caption" fontWeight={700}>
              {formatFboFloat(pallets)}{capacity ? ` / ${capacity}` : ''} пал.
            </Typography>
          </Stack>
          <Box sx={(theme) => ({
            mt: 0.5,
            height: 6,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            overflow: 'hidden',
          })}>
            <Box sx={(theme) => ({
              width: `${Math.min(Math.max(Number(fillPercent) || 0, 0), 100)}%`,
              height: '100%',
              bgcolor: isTruckUnderloaded(truck) ? theme.palette.warning.main : theme.palette.primary.main,
            })} />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">{formatFboDate(getTruckDate(truck))}</Typography>
          <Typography variant="caption" fontWeight={700}>
            {fillPercent == null ? '—' : `${formatFboFloat(fillPercent)}%`}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function FboTruckCalendar({
  truckPlan,
  readOnly = true,
  editable = false,
  dndEnabled = false,
  onTruckOpen,
  onRegionRemove,
  onTruckDrop,
  emptyMessage = 'План фур пока пуст',
  showDndHint = false,
  busy = false,
}) {
  const [draggingTruckId, setDraggingTruckId] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const dragPayloadRef = useRef(null);
  const consumedDropRef = useRef(false);
  const canEdit = editable && !readOnly && !busy;
  const canDnd = canEdit && dndEnabled;

  if (!truckPlan?.trucks?.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Paper>
    );
  }

  const handleDragStart = (event, truck) => {
    if (!canDnd) return;
    const truckId = getTruckId(truck);
    if (!truckId) return;
    const payload = {
      truckId: String(truckId),
      sourceDate: getTruckDate(truck) || null,
      sourceRegion: getTruckRegion(truck) || null,
    };
    dragPayloadRef.current = payload;
    consumedDropRef.current = false;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(TRUCK_DRAG_MIME, JSON.stringify(payload));
    event.dataTransfer.setData('text/plain', payload.truckId);
    setDraggingTruckId(payload.truckId);
  };

  const resetDrag = () => {
    dragPayloadRef.current = null;
    consumedDropRef.current = false;
    setDraggingTruckId(null);
    setDragOverCell(null);
  };

  const targetAllowed = (region) => (
    canDnd
    && canMoveTruckToRegion(
      { okrug: dragPayloadRef.current?.sourceRegion },
      region.value,
    )
  );

  const handleDrop = (event, date, region) => {
    event.preventDefault();
    event.stopPropagation();
    if (consumedDropRef.current || !targetAllowed(region)) return;
    consumedDropRef.current = true;
    let payload = dragPayloadRef.current;
    const rawPayload = event.dataTransfer.getData(TRUCK_DRAG_MIME);
    if (rawPayload) {
      try {
        payload = JSON.parse(rawPayload);
      } catch {
        payload = dragPayloadRef.current;
      }
    }
    const truckId = payload?.truckId || event.dataTransfer.getData('text/plain');
    dragPayloadRef.current = null;
    setDraggingTruckId(null);
    setDragOverCell(null);
    if (!truckId) return;
    const truck = truckPlan.trucks.find((item) => String(getTruckId(item)) === String(truckId));
    if (!canMoveTruckToRegion(truck, region.value)) return;
    onTruckDrop?.({
      truckId,
      shipDate: date.value,
      sourceDate: payload?.sourceDate,
      region: region.value,
    });
  };

  return (
    <Stack spacing={1.5}>
      {canDnd && showDndHint && (
        <Alert severity="info" icon={<TruckIcon fontSize="inherit" />}>
          Перетаскивайте фуры за маркер только между датами одного региона.
        </Alert>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '70vh',
          overflow: 'auto',
          border: (theme) => `1px solid ${theme.palette.outlineVariant || theme.palette.divider}`,
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: Math.max(760, 220 + truckPlan.dates.length * 220) }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 220, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>
                Регион / дата
              </TableCell>
              {truckPlan.dates.map((date) => (
                <TableCell key={date.value} align="center" sx={{ minWidth: 190 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {[date.weekday, date.label].filter(Boolean).join(' ').replace(/^./, (char) => char.toLocaleUpperCase('ru'))}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {truckPlan.regions.map((region) => (
              <TableRow key={region.value} hover>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', minWidth: 220 }}>
                  <Stack spacing={1}>
                    <Tooltip title={region.value}>
                      <Typography variant="body2" fontWeight={700}>{region.label || region.value}</Typography>
                    </Tooltip>
                    {canEdit && onRegionRemove && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => onRegionRemove(region)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Убрать регион
                      </Button>
                    )}
                  </Stack>
                </TableCell>
                {truckPlan.dates.map((date) => {
                  const cellKey = truckCellKey(date.value, region.value);
                  const cellTrucks = truckPlan.cells[cellKey] || [];
                  const allowed = targetAllowed(region);
                  const isDragOver = allowed && dragOverCell === cellKey;
                  return (
                    <TableCell
                      key={`${region.value}_${date.value}`}
                      onDragOver={canDnd ? (event) => {
                        if (!allowed) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                      } : undefined}
                      onDragEnter={canDnd ? (event) => {
                        if (!allowed) return;
                        event.preventDefault();
                        setDragOverCell(cellKey);
                      } : undefined}
                      onDragLeave={canDnd ? (event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) setDragOverCell(null);
                      } : undefined}
                      onDrop={canDnd ? (event) => handleDrop(event, date, region) : undefined}
                      sx={(theme) => ({
                        verticalAlign: 'top',
                        minWidth: 190,
                        height: 116,
                        border: `1px ${allowed ? 'dashed' : 'solid'} ${isDragOver ? theme.palette.primary.main : alpha(theme.palette.divider, 0.7)}`,
                        bgcolor: isDragOver
                          ? alpha(theme.palette.primary.main, 0.12)
                          : (cellTrucks.length ? 'background.paper' : alpha(theme.palette.primary.main, 0.025)),
                        opacity: draggingTruckId && !allowed ? 0.55 : 1,
                        transition: 'opacity 120ms ease, background-color 120ms ease, border-color 120ms ease',
                      })}
                    >
                      {cellTrucks.length ? (
                        <Stack spacing={1}>
                          {cellTrucks.map((truck) => (
                            <FboTruckCard
                              key={getTruckId(truck) ?? `draft_${truck._idx}`}
                              truck={truck}
                              readOnly={readOnly}
                              editable={editable}
                              dndEnabled={dndEnabled}
                              onOpen={onTruckOpen ? () => onTruckOpen(getTruckId(truck), truck) : undefined}
                              dragging={String(getTruckId(truck)) === String(draggingTruckId)}
                              onDragStart={(event) => handleDragStart(event, truck)}
                              onDragEnd={resetDrag}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Box sx={{ minHeight: 82, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {allowed ? 'Перетащите сюда' : '—'}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export function FboTruckDetailsDialog({
  truck,
  open,
  onClose,
  readOnly = true,
  editable = false,
  dndEnabled = false,
  trucks = [],
  onTruckUpdate,
  onTruckDelete,
  onItemUpdate,
  onItemDelete,
  onItemAdd,
  onProductSearch,
  fullScreen = false,
  busy = false,
}) {
  const theme = useTheme();
  const responsiveMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const mobileLayout = fullScreen || responsiveMobile;
  const canEdit = editable && !readOnly;
  const items = truck?.items || truck?.composition || truck?.rows || [];
  const truckId = getTruckId(truck);
  const [truckDraft, setTruckDraft] = useState({ ship_date: '', capacity_pallets: 16, notes: '' });
  const [itemDrafts, setItemDrafts] = useState({});
  const [addDraft, setAddDraft] = useState({ product: null, qty: '' });
  const [productInput, setProductInput] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productSearchError, setProductSearchError] = useState('');
  const [validation, setValidation] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [query, setQuery] = useState('');
  const [orderBy, setOrderBy] = useState('product');
  const [order, setOrder] = useState('asc');
  const [addExpanded, setAddExpanded] = useState(false);
  const productInputRef = useRef(null);

  useEffect(() => {
    if (!truck || !open) return;
    setTruckDraft({
      ship_date: getTruckDate(truck) || '',
      capacity_pallets: getTruckCapacity(truck) || 16,
      notes: truck.notes || '',
    });
    setItemDrafts(Object.fromEntries(
      (truck.items || []).filter((item) => item.id).map((item) => [
        item.id,
        { qty: item.qty ?? '', truck_id: item.truck_id || getTruckId(truck) },
      ]),
    ));
    setAddDraft({ product: null, qty: '' });
    setProductInput('');
    setProductOptions([]);
    setProductSearchError('');
    setValidation({});
  }, [truck, open]);

  useEffect(() => {
    if (!open) return;
    setActiveTab(0);
    setQuery('');
    setOrderBy('product');
    setOrder('asc');
    setAddExpanded(false);
  }, [open, truckId]);

  useEffect(() => {
    if (addExpanded) productInputRef.current?.focus();
  }, [addExpanded]);

  useEffect(() => {
    const search = productInput.trim();
    if (!addExpanded || addDraft.product || search.length < 2 || !onProductSearch) {
      setProductOptions([]);
      setProductLoading(false);
      setProductSearchError('');
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setProductLoading(true);
      setProductSearchError('');
      try {
        const options = await onProductSearch(search);
        if (active) setProductOptions(Array.isArray(options) ? options : []);
      } catch (error) {
        if (active) {
          setProductOptions([]);
          setProductSearchError(error?.response?.data?.message || error?.message || 'Не удалось найти товары');
        }
      } finally {
        if (active) setProductLoading(false);
      }
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [addDraft.product, addExpanded, onProductSearch, productInput]);

  const patchItemDraft = (itemId, patch) => {
    setItemDrafts((current) => ({ ...current, [itemId]: { ...(current[itemId] || {}), ...patch } }));
    setValidation((current) => ({ ...current, [`item_${itemId}`]: null }));
  };

  const itemQty = (item, draft = itemDrafts[item.id]) => Number(draft?.qty ?? item.qty ?? item.quantity ?? item.to_supply ?? 0);
  const itemPallets = (item, draft = itemDrafts[item.id]) => {
    const perPallet = Number(item.units_per_pallet);
    return perPallet > 0 ? itemQty(item, draft) / perPallet : Number(item.pallets || 0);
  };
  const itemDestination = (item, draft = itemDrafts[item.id]) => (
    draft?.truck_id ?? item.truck_id ?? truckId
  );
  const itemIsDirty = (item) => {
    const draft = itemDrafts[item.id];
    if (!draft) return false;
    return String(draft.qty ?? '') !== String(item.qty ?? '')
      || String(draft.truck_id ?? '') !== String(item.truck_id ?? truckId ?? '');
  };

  const projectedPalletsForTruck = (destinationId) => {
    const destination = trucks.find((candidate) => String(getTruckId(candidate)) === String(destinationId));
    let projected = Number(getTruckPallets(destination) || 0);
    items.forEach((item) => {
      if (!item.id) return;
      const originalDestination = item.truck_id ?? truckId;
      const nextDestination = itemDestination(item);
      const originalPallets = Number(item.pallets || (
        Number(item.units_per_pallet) > 0 ? Number(item.qty || 0) / Number(item.units_per_pallet) : 0
      ));
      const nextPallets = itemPallets(item);
      if (String(originalDestination) === String(destinationId)) projected -= originalPallets;
      if (String(nextDestination) === String(destinationId) && Number.isFinite(nextPallets)) projected += nextPallets;
    });
    return projected;
  };

  const validateTruck = () => {
    const nextCapacity = Number(truckDraft.capacity_pallets);
    if (!truckDraft.ship_date) return 'Укажите дату отправки';
    if (![16, 32].includes(nextCapacity)) return 'Вместимость должна быть 16 или 32 палеты';
    const projected = projectedPalletsForTruck(truckId);
    if (projected > nextCapacity) return `По текущему составу будет ${formatFboFloat(projected)} пал. — выберите большую вместимость или перенесите товары`;
    return null;
  };

  const validateItemQty = (item, draft = itemDrafts[item.id]) => {
    const qty = Number(draft?.qty);
    if (!Number.isInteger(qty) || qty <= 0) return 'Введите целое количество больше нуля';
    const perPallet = Number(item.units_per_pallet);
    if (perPallet > 0 && qty % perPallet !== 0) return `Количество должно быть кратно ${perPallet} шт.`;
    const destinationId = itemDestination(item, draft);
    const destination = trucks.find((candidate) => String(getTruckId(candidate)) === String(destinationId));
    if (destination) {
      const destinationCapacity = Number(getTruckCapacity(destination) || 0);
      const projectedPallets = projectedPalletsForTruck(destinationId);
      if (destinationCapacity > 0 && projectedPallets > destinationCapacity) {
        return `После изменения будет ${formatFboFloat(projectedPallets)} пал. при вместимости ${formatFboFloat(destinationCapacity)} пал.`;
      }
    }
    return null;
  };

  const visibleItems = useMemo(
    () => prepareFboTruckItems(items, { query, orderBy, order }),
    [items, query, orderBy, order],
  );
  const draftTotals = useMemo(() => {
    const currentItems = items.filter((item) => String(itemDestination(item)) === String(truckId));
    const qty = currentItems.reduce((sum, item) => {
      const value = itemQty(item);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    const pallets = currentItems.reduce((sum, item) => {
      const value = itemPallets(item);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    const capacity = Number(truckDraft.capacity_pallets || getTruckCapacity(truck) || 0);
    return {
      products: currentItems.length,
      qty,
      pallets,
      capacity,
      balance: capacity - pallets,
    };
  }, [items, itemDrafts, truckDraft.capacity_pallets, truckId, truck]);
  const truckDirty = Boolean(truck) && (
    String(truckDraft.ship_date) !== String(getTruckDate(truck) || '')
    || Number(truckDraft.capacity_pallets) !== Number(getTruckCapacity(truck) || 16)
    || String(truckDraft.notes) !== String(truck.notes || '')
  );
  const truckError = canEdit ? validateTruck() : null;
  const displayStatus = draftTotals.balance < 0
    ? { color: 'error', label: 'Перегруз' }
    : draftTotals.balance > 0
      ? { color: 'warning', label: 'Недогруз' }
      : draftTotals.capacity > 0
        ? { color: 'success', label: 'Полная' }
        : getTruckStatusMeta(truck);

  const handleSort = (field) => {
    if (orderBy === field) setOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
    else {
      setOrderBy(field);
      setOrder('asc');
    }
  };
  const sortLabel = (field, label, align = 'left') => (
    <TableSortLabel
      active={orderBy === field}
      direction={orderBy === field ? order : 'asc'}
      onClick={() => handleSort(field)}
      sx={align === 'right' ? { justifyContent: 'flex-end', width: '100%' } : undefined}
    >
      {label}
    </TableSortLabel>
  );

  const renderItemEditor = (item, mobile = false, showControls = true, showIdentity = true) => {
    const draft = itemDrafts[item.id] || {};
    const dirty = item.id ? itemIsDirty(item) : false;
    const inlineError = canEdit && item.id && dirty ? validateItemQty(item, draft) : null;
    const error = validation[`item_${item.id}`] || inlineError;
    const selectedTruckId = itemDestination(item, draft);
    const draftPallets = itemPallets(item, draft);

    return (
      <Stack spacing={1.25}>
        {showIdentity && (
          <Box>
            <Typography variant="body2" fontWeight={700}>{getFboTruckItemName(item)}</Typography>
            {(item.nm_id || item.tech_size) && (
              <Typography variant="caption" color="text.secondary" component="div">
                {item.nm_id ? `nmID ${item.nm_id}` : ''}{item.tech_size ? ` · разм. ${item.tech_size}` : ''}
              </Typography>
            )}
          </Box>
        )}
        {mobile && (
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">Баркод: {item.barcode || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">Шт./палета: {formatFboNumber(item.units_per_pallet)}</Typography>
            <Typography variant="caption" color="text.secondary">Палеты: {formatFboFloat(draftPallets)}</Typography>
            <Typography variant="caption" color="text.secondary">Приход: {formatFboDate(item.arrival_date || truck.arrival_date)}</Typography>
          </Stack>
        )}
        {showControls && canEdit && item.id && (
          <Stack direction={mobile ? 'column' : 'row'} spacing={1} alignItems={mobile ? 'stretch' : 'flex-start'}>
            <TextField
              fullWidth={mobile}
              size="small"
              type="number"
              label={mobile ? 'Количество' : undefined}
              sx={mobile ? undefined : { width: 120 }}
              value={draft.qty ?? item.qty ?? ''}
              onChange={(event) => patchItemDraft(item.id, { qty: event.target.value })}
              error={Boolean(error)}
              helperText={error || ''}
              disabled={busy}
              inputProps={{ min: 1, step: 1 }}
            />
            <FormControl fullWidth={mobile} size="small" sx={mobile ? undefined : { minWidth: 190 }}>
              <InputLabel>Переместить в</InputLabel>
              <Select
                label="Переместить в"
                value={selectedTruckId || ''}
                onChange={(event) => patchItemDraft(item.id, { truck_id: event.target.value })}
                disabled={busy}
              >
                {trucks.map((candidate) => (
                  <MenuItem
                    key={getTruckId(candidate)}
                    value={getTruckId(candidate)}
                    disabled={String(getTruckRegion(candidate)) !== String(getTruckRegion(truck))}
                  >
                    {formatFboDate(getTruckDate(candidate))} · {getTruckTypeLabel(candidate)} пал.
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={0.5} justifyContent={mobile ? 'stretch' : 'flex-end'}>
              <Button
                fullWidth={mobile}
                size="small"
                variant={dirty ? 'contained' : 'outlined'}
                startIcon={<SaveIcon />}
                disabled={!dirty || Boolean(error) || busy}
                onClick={() => {
                  const nextError = validateItemQty(item, draft);
                  setValidation((current) => ({ ...current, [`item_${item.id}`]: nextError }));
                  if (!nextError) onItemUpdate?.(item, draft);
                }}
              >
                Сохранить
              </Button>
              <Tooltip title="Удалить товар">
                <span>
                  <IconButton color="error" disabled={busy} onClick={() => onItemDelete?.(item)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  };

  const compositionContent = (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          placeholder="Артикул, название, nmID, размер или баркод"
          onChange={(event) => setQuery(event.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        {mobileLayout && (
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Сортировка</InputLabel>
            <Select
              label="Сортировка"
              value={`${orderBy}:${order}`}
              onChange={(event) => {
                const [field, direction] = event.target.value.split(':');
                setOrderBy(field);
                setOrder(direction);
              }}
            >
              {[
                ['product', 'Товар'],
                ['barcode', 'Баркод'],
                ['qty', 'Количество'],
                ['units_per_pallet', 'Шт./палета'],
                ['pallets', 'Палеты'],
              ].flatMap(([field, label]) => [
                <MenuItem key={`${field}:asc`} value={`${field}:asc`}>{label} · по возрастанию</MenuItem>,
                <MenuItem key={`${field}:desc`} value={`${field}:desc`}>{label} · по убыванию</MenuItem>,
              ])}
            </Select>
          </FormControl>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Показано {visibleItems.length} из {items.length}
        </Typography>
      </Stack>

      {!mobileLayout ? (
        <TableContainer sx={{ maxHeight: '52vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{sortLabel('product', 'Товар')}</TableCell>
                <TableCell>{sortLabel('barcode', 'Баркод')}</TableCell>
                <TableCell align="right">{sortLabel('qty', 'Количество', 'right')}</TableCell>
                <TableCell align="right">{sortLabel('units_per_pallet', 'Шт./палета', 'right')}</TableCell>
                <TableCell align="right">{sortLabel('pallets', 'Палеты', 'right')}</TableCell>
                {canEdit && <TableCell sx={{ minWidth: 320 }}>Редактирование</TableCell>}
                <TableCell>Приход</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!visibleItems.length && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : 6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {items.length ? 'По вашему запросу товары не найдены' : (canEdit ? 'Состав фуры пустой' : 'Состав не передан в контракте')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visibleItems.map((item, index) => {
                const dirty = item.id && itemIsDirty(item);
                const draft = itemDrafts[item.id];
                return (
                  <TableRow
                    key={item.id ?? `${item.barcode || item.article || index}_${index}`}
                    sx={dirty ? { bgcolor: alpha(theme.palette.primary.main, 0.08) } : undefined}
                  >
                    <TableCell>{renderItemEditor(item, false, false, true)}</TableCell>
                    <TableCell>{item.barcode || '—'}</TableCell>
                    <TableCell align="right">
                      {formatFboNumber(itemDrafts[item.id]?.qty ?? item.qty ?? item.quantity ?? item.to_supply)}
                    </TableCell>
                    <TableCell align="right">{formatFboNumber(item.units_per_pallet)}</TableCell>
                    <TableCell align="right">{formatFboFloat(itemPallets(item, draft))}</TableCell>
                    {canEdit && <TableCell>{item.id ? renderItemEditor(item, false, true, false) : '—'}</TableCell>}
                    <TableCell>{formatFboDate(item.arrival_date || truck.arrival_date)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Stack spacing={1}>
          {!visibleItems.length && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {items.length ? 'По вашему запросу товары не найдены' : (canEdit ? 'Состав фуры пустой' : 'Состав не передан в контракте')}
              </Typography>
            </Paper>
          )}
          {visibleItems.map((item, index) => (
            <Paper
              key={item.id ?? `${item.barcode || item.article || index}_${index}`}
              variant="outlined"
              sx={item.id && itemIsDirty(item) ? { p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.08) } : { p: 1.5 }}
            >
              {renderItemEditor(item, true)}
            </Paper>
          ))}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" justifyContent="space-between">
          <Typography variant="body2"><b>Товары:</b> {draftTotals.products}</Typography>
          <Typography variant="body2"><b>Штуки:</b> {formatFboNumber(draftTotals.qty)}</Typography>
          <Typography variant="body2"><b>Палеты:</b> {formatFboFloat(draftTotals.pallets)}</Typography>
          <Typography variant="body2" color={draftTotals.balance < 0 ? 'error.main' : 'success.main'} fontWeight={700}>
            {draftTotals.balance < 0
              ? `Перегруз: ${formatFboFloat(Math.abs(draftTotals.balance))} пал.`
              : `Свободно: ${formatFboFloat(draftTotals.balance)} пал.`}
          </Typography>
        </Stack>
      </Paper>

      {canEdit && (
        <Paper variant="outlined">
          <Button
            fullWidth
            color="inherit"
            startIcon={<AddIcon />}
            endIcon={addExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setAddExpanded((current) => !current)}
            disabled={busy}
            sx={{ justifyContent: 'space-between', px: 2, py: 1.25 }}
          >
            Добавить товар
          </Button>
          <Collapse in={addExpanded}>
            <Divider />
            <Grid container spacing={1.5} sx={{ p: 2 }} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={productOptions}
                  value={addDraft.product}
                  inputValue={productInput}
                  loading={productLoading}
                  disabled={busy}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(option, value) => (
                    String(option.organization) === String(value.organization)
                    && String(option.product_key) === String(value.product_key)
                    && String(option.barcode) === String(value.barcode)
                  )}
                  getOptionLabel={(option) => [
                    option.article || option.display_name || 'Без артикула',
                    option.nm_id ? `nmID ${option.nm_id}` : null,
                    option.barcode ? `ШК ${option.barcode}` : null,
                  ].filter(Boolean).join(' · ')}
                  noOptionsText={
                    productInput.trim().length < 2
                      ? 'Введите минимум 2 символа'
                      : productSearchError || 'Подходящие товары не найдены'
                  }
                  onInputChange={(_, value, reason) => {
                    setProductInput(value);
                    if (reason === 'clear' || reason === 'input') {
                      setAddDraft((current) => ({ ...current, product: null }));
                    }
                    setValidation((current) => ({ ...current, add: null }));
                  }}
                  onChange={(_, product) => {
                    setAddDraft((current) => ({ ...current, product }));
                    setProductInput(product ? [
                      product.article || product.display_name || 'Без артикула',
                      product.nm_id ? `nmID ${product.nm_id}` : null,
                      product.barcode ? `ШК ${product.barcode}` : null,
                    ].filter(Boolean).join(' · ') : '');
                    setValidation((current) => ({ ...current, add: null }));
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={`${option.organization}_${option.product_key}_${option.barcode}`}>
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {option.article || option.display_name || option.product_name || 'Без артикула'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            option.nm_id ? `nmID ${option.nm_id}` : null,
                            option.barcode ? `ШК ${option.barcode}` : null,
                            option.tech_size ? `разм. ${option.tech_size}` : null,
                            option.organization,
                          ].filter(Boolean).join(' · ')}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={productInputRef}
                      label="Баркод, артикул или nmID"
                      error={Boolean(productSearchError)}
                      helperText={productSearchError || 'Начните вводить идентификатор товара'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {productLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Количество"
                  value={addDraft.qty}
                  error={Boolean(validation.add)}
                  helperText={validation.add || 'Должно быть кратно палете товара'}
                  disabled={busy}
                  onChange={(event) => {
                    setAddDraft((current) => ({ ...current, qty: event.target.value }));
                    setValidation((current) => ({ ...current, add: null }));
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth={mobileLayout}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={busy}
                  onClick={async () => {
                    const qty = Number(addDraft.qty);
                    if (!addDraft.product) {
                      setValidation((current) => ({ ...current, add: 'Выберите товар из списка' }));
                      return;
                    }
                    if (!Number.isInteger(qty) || qty <= 0) {
                      setValidation((current) => ({ ...current, add: 'Введите целое количество больше нуля' }));
                      return;
                    }
                    const product = addDraft.product;
                    const ok = await onItemAdd?.(truck, {
                      organization: product.organization,
                      product_key: product.product_key,
                      nm_id: product.nm_id,
                      barcode: product.barcode,
                      qty: addDraft.qty,
                    });
                    if (ok !== false) {
                      setAddDraft({ product: null, qty: '' });
                      setProductInput('');
                      setProductOptions([]);
                      setAddExpanded(false);
                    }
                  }}
                >
                  {busy ? 'Добавление…' : 'Добавить в фуру'}
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      )}
    </Stack>
  );

  const settingsContent = canEdit && (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Дата отправки"
              InputLabelProps={{ shrink: true }}
              value={truckDraft.ship_date}
              disabled={busy}
              onChange={(event) => setTruckDraft((current) => ({ ...current, ship_date: event.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Вместимость</InputLabel>
              <Select
                label="Вместимость"
                value={truckDraft.capacity_pallets}
                disabled={busy}
                onChange={(event) => setTruckDraft((current) => ({ ...current, capacity_pallets: event.target.value }))}
              >
                <MenuItem value={16}>16 палет</MenuItem>
                <MenuItem value={32}>32 палеты</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Комментарий"
              value={truckDraft.notes}
              disabled={busy}
              onChange={(event) => setTruckDraft((current) => ({ ...current, notes: event.target.value }))}
            />
          </Grid>
        </Grid>
      </Paper>
      {(validation.truck || (truckDirty && truckError)) && (
        <Alert severity="error">{validation.truck || truckError}</Alert>
      )}
      {dndEnabled && (
        <Alert severity="info">Дату также можно менять перетаскиванием фуры в календаре.</Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          disabled={busy}
          onClick={() => onTruckDelete?.(truck)}
        >
          Удалить фуру
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={busy || !truckDirty || Boolean(truckError)}
          onClick={() => {
            const error = validateTruck();
            setValidation((current) => ({ ...current, truck: error }));
            if (!error) onTruckUpdate?.(truck, truckDraft);
          }}
        >
          {busy ? 'Сохранение…' : 'Сохранить настройки'}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth fullScreen={mobileLayout}>
      <DialogTitle sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper', pb: canEdit ? 0 : 2 }}>
        {truck && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="titleLarge" component="div">Фура {getTruckTypeLabel(truck)} пал.</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getTruckRegion(truck) || 'Регион не указан'} · {formatFboDate(truckDraft.ship_date || getTruckDate(truck))}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={<TruckIcon />}
                  color={draftTotals.balance < 0 ? 'error' : 'primary'}
                  label={`${formatFboFloat(draftTotals.pallets)} / ${formatFboFloat(draftTotals.capacity)} пал.`}
                />
                {!mobileLayout && <Chip color={displayStatus.color} variant="outlined" label={displayStatus.label} />}
              </Stack>
            </Stack>
            {canEdit && (
              <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant={mobileLayout ? 'fullWidth' : 'standard'}>
                <Tab label="Состав фуры" disabled={busy} />
                <Tab label="Настройки" disabled={busy} />
              </Tabs>
            )}
          </Stack>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {truck && (canEdit ? (activeTab === 0 ? compositionContent : settingsContent) : compositionContent)}
      </DialogContent>
      <DialogActions sx={{ position: 'sticky', bottom: 0, zIndex: 3, bgcolor: 'background.paper', px: 2, py: 1.5 }}>
        <Button fullWidth={mobileLayout} onClick={onClose} disabled={busy}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
